/* One group: its wall, its people, and who has been invited.
 *
 * Reached by tapping a row on the Groups list. The client's sketch draws this
 * as an accordion; a page with tabs carries what a group actually is — a wall
 * people post to, a roster, and an invite list — and gives it a URL that can
 * be handed to a moderator.
 *
 * CALLS AND MESSAGES ARE NOT HERE, and that is deliberate rather than
 * unfinished: both belong to the CHURCH and choose their audience, which may
 * be this group or several or everybody. Putting either inside one group
 * would make "the whole church" impossible to say. Both have their own page.
 *
 * MODERATOR IS SCOPED TO THIS GROUP (migration 0013). Promoting somebody here
 * makes them a moderator of THIS group and no other — which is what a button
 * on a group's page ought to mean. Church-wide moderators are set on the
 * Permissions page instead.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTabParam } from '@/lib/useTabParam'
import { ArrowLeft, Check, Loader2, MoreHorizontal, Users } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Composer } from '@/features/feed/Composer'
import { PostCard } from '@/features/feed/PostCard'
import { EventsPanel } from '@/features/church/EventsPanel'
import { GroupMeetingsPanel } from '@/features/church/CallsPage'
import { GroupSettingsPanel } from '@/features/church/GroupSettingsPanel'
import { InvitePanel } from '@/features/church/InvitePanel'
import { PollsPanel } from '@/features/church/PollsPanel'
import {
  useGroup,
  useAnswerJoinRequest,
  useJoinRequests,
  useCan,
  useFeatures,
  useMyGroups,
  useGroupFeed,
  useGroupMembers,
  useMe,
  useSetGroupMemberRole,
  useRoles,
} from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'
import type { Member } from '@/types/api'

type Tab =
  | 'feed' | 'members' | 'events' | 'meetings' | 'polls'
  | 'requests' | 'invites' | 'settings'

/* Every tab this screen can show, for validating ?tab= against. Not derived
   from TABS, because that list is filtered by permission — a moderator
   reloading on ?tab=requests must not be bounced to Feed while the
   permissions are still loading. */
const ALL_TABS: readonly Tab[] = [
  'feed', 'members', 'events', 'meetings', 'polls', 'requests', 'invites',
  'settings',
]

export function GroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const [tab, setTab] = useTabParam<Tab>(ALL_TABS, 'feed')

  const group = useGroup(churchId, groupId)

  /* Whether this person may answer join requests: a church admin, or a
     moderator OF THIS GROUP. my-groups already carries is_moderator per group,
     so no extra endpoint — and it is `enabled` only for non-admins, who are
     the only ones whose answer it changes. */
  const isChurchAdmin = me?.role === 'church_admin' || me?.role === 'super_admin'
  const myGroups = useMyGroups(!isChurchAdmin)
  const canApprove =
    isChurchAdmin ||
    Boolean(myGroups.data?.items.find((x) => x.id === groupId)?.is_moderator)

  const can = useCan(churchId, groupId)
  const features = useFeatures()

  const pending = useJoinRequests(
    canApprove ? churchId : null,
    canApprove ? groupId : null,
  )

  if (!me) return null

  if (group.isLoading) {
    return (
      <div className="space-y-s4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  // A deleted group, or one belonging to another church: the API returns 404
  // for both, and this page must not pretend otherwise.
  if (group.isError || !group.data) {
    return (
      <Card className="p-s8 text-center">
        <Users className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">This group is not available</p>
        <p className="mt-1 text-base text-ink-2">
          It may have been removed, or it belongs to another church.
        </p>
        <Button variant="secondary" className="mt-s4" asChild>
          <Link to="/groups">Back to Groups</Link>
        </Button>
      </Card>
    )
  }

  const g = group.data
  const isAdmin = isChurchAdmin

  const TABS: { key: Tab; label: string }[] = [
    { key: 'feed', label: 'Feed' },
    { key: 'members', label: `Members (${g.member_count})` },
    // Events, Meetings and Polls only where the church offers them.
    ...(features.events
      ? ([{ key: 'events', label: 'Events' }] as { key: Tab; label: string }[])
      : []),
    // The group's meetings and lives, on its own page.
    ...(features.anyMeetings
      ? ([{ key: 'meetings', label: 'Meetings' }] as { key: Tab; label: string }[])
      : []),
    ...(features.polls
      ? ([{ key: 'polls', label: 'Polls' }] as { key: Tab; label: string }[])
      : []),
    // Requests come BEFORE the admin-only tabs: a group moderator can answer
    // them without being a church admin, which is the whole point — otherwise
    // every request in every group queues behind one person.
    ...(canApprove
      ? ([
          {
            key: 'requests',
            label: pending.data?.length
              ? `Requests (${pending.data.length})`
              : 'Requests',
          },
        ] as { key: Tab; label: string }[])
      : []),
    // Inviting and renaming belong to whoever runs the church, so those two
    // tabs are not offered to an ordinary member.
    // Invites follows the PERMISSION — a moderator may invite, and the
    // endpoint enforces members.invite. Settings stays with the church admin:
    // renaming a group is not a moderator's job.
    ...(can('members.invite')
      ? ([{ key: 'invites', label: 'Invites' }] as { key: Tab; label: string }[])
      : []),
    ...(isAdmin
      ? ([{ key: 'settings', label: 'Settings' }] as { key: Tab; label: string }[])
      : []),
  ]

  return (
    <div className="space-y-s4">
      <div>
        <Link
          to="/groups"
          className="inline-flex items-center gap-s2 text-base font-semibold text-ink-2 transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Groups
        </Link>
      </div>

      <header className="flex items-start gap-s3">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
          <Users className="h-6 w-6" aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{g.name}</h1>
          <p className="mt-1 text-base text-ink-2">
            {g.member_count.toLocaleString()}{' '}
            {g.member_count === 1 ? 'member' : 'members'}
            {g.description ? ` · ${g.description}` : ''}
          </p>
        </div>
      </header>

      <div className="flex gap-s1 border-b border-line-2" role="tablist">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'relative px-s4 py-s3 text-md font-semibold transition-colors',
              tab === key ? 'text-brand' : 'text-ink-2 hover:text-ink',
            )}
          >
            {label}
            {tab === key ? (
              <span
                className="absolute inset-x-0 -bottom-px h-[3px] rounded-t bg-brand"
                aria-hidden
              />
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'feed' ? (
        <GroupFeed groupId={g.id} groupName={g.name} />
      ) : null}
      {tab === 'members' ? (
        <MembersCard churchId={churchId!} groupId={g.id} isAdmin={isAdmin} />
      ) : null}
      {tab === 'events' ? (
        features.events ? (
          <EventsPanel churchId={churchId!} groupId={g.id} />
        ) : (
          <Unavailable what="Events aren't" />
        )
      ) : null}
      {tab === 'meetings' ? (
        features.anyMeetings ? (
          <GroupMeetingsPanel churchId={churchId!} group={{ id: g.id, name: g.name }} />
        ) : (
          <Unavailable what="Meetings aren't" />
        )
      ) : null}
      {tab === 'polls' ? (
        features.polls ? (
          <PollsPanel churchId={churchId!} groupId={g.id} />
        ) : (
          <Unavailable what="Polls aren't" />
        )
      ) : null}
      {tab === 'requests' ? (
        <JoinRequestsCard churchId={churchId!} groupId={g.id} />
      ) : null}

      {tab === 'invites' ? (
        <InvitePanel churchId={churchId!} groupId={g.id} />
      ) : null}
      {tab === 'settings' ? (
        <GroupSettingsPanel churchId={churchId!} group={g} />
      ) : null}
    </div>
  )
}

/** A tab reached by a link to something the church does not offer. */
function Unavailable({ what }: { what: string }) {
  return (
    <Card className="p-s6 text-center text-base text-ink-2">
      {what} available for your church.
    </Card>
  )
}

/* -------------------------------------------------------------------- feed */

function GroupFeed({
  groupId,
  groupName,
}: {
  groupId: string
  groupName: string
}) {
  const feed = useGroupFeed(groupId)
  const { data: me } = useMe()
  const posts = feed.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="space-y-s3">
      <Composer group={{ id: groupId, name: groupName }} />

      {feed.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {/* A 403 here means the viewer is in the church but not this group. */}
      {feed.isError ? (
        <Card className="p-s6 text-center">
          <p className="font-semibold">This wall is for members of the group.</p>
          <p className="mt-1 text-base text-ink-2">
            Ask a church admin to add you.
          </p>
        </Card>
      ) : null}

      {!feed.isLoading && !feed.isError && !posts.length ? (
        <Card className="p-s8 text-center">
          <p className="text-md font-bold">Nothing here yet</p>
          <p className="mt-1 text-base text-ink-2">
            Posts here are seen only by members of {groupName}.
          </p>
        </Card>
      ) : null}

      {posts.map((post) => (
        <PostCard key={post.id} post={post} viewerId={me?.id} />
      ))}

      {feed.hasNextPage ? (
        <Button
          variant="secondary"
          full
          onClick={() => feed.fetchNextPage()}
          loading={feed.isFetchingNextPage}
        >
          Show more
        </Button>
      ) : null}
    </div>
  )
}

/* ----------------------------------------------------------------- members */

function MembersCard({
  churchId,
  groupId,
  isAdmin,
}: {
  churchId: string
  groupId: string
  isAdmin: boolean
}) {
  const members = useGroupMembers(churchId, groupId)

  return (
    <Card>
      {members.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {!members.isLoading && !members.data?.length ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          Nobody is in this group yet.
        </p>
      ) : null}

      {members.data?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse">
            <thead>
              <tr className="border-b border-line-2 text-left">
                {['Name', 'Mobile', 'Role', ''].map((h) => (
                  <th
                    key={h}
                    className="px-s4 py-s2 text-sm font-bold uppercase tracking-wide text-ink-2"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  churchId={churchId}
                  groupId={groupId}
                  isAdmin={isAdmin}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {members.isError ? (
        <div className="p-s5 text-center">
          <p className="font-semibold">The members did not load.</p>
          <Button
            variant="secondary"
            className="mt-s3"
            onClick={() => members.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : null}
    </Card>
  )
}

function MemberRow({
  member,
  churchId,
  groupId,
  isAdmin,
}: {
  member: Member
  churchId: string
  groupId: string
  isAdmin: boolean
}) {
  const setRole = useSetGroupMemberRole(churchId, groupId)
  const roles = useRoles(churchId)

  return (
    <tr className="border-b border-line-2 last:border-0">
      <td className="px-s4 py-s3">
        <div className="flex items-center gap-s3">
          <Avatar name={member.name} size="sm" />
          <span className="font-semibold">{member.name}</span>
        </div>
      </td>
      <td className="px-s4 py-s3 text-base text-ink-2">
        {member.mobile ?? '—'}
      </td>
      <td className="px-s4 py-s3">
        {/* THE ROLE'S OWN NAME. This used to print a hardcoded "Moderator" or
            "Member" from the is_moderator boolean, which is how somebody wore
            a Moderator badge while holding a participant's permissions — and
            why a church that renames the role to "Youth Lead" would still have
            seen "Moderator" here. */}
        <span
          className={cn(
            'inline-block rounded-pill px-s2 py-0.5 text-sm font-semibold',
            member.role === 'church_admin'
              ? 'bg-warning-soft text-warning-ink'
              : member.is_moderator
                ? 'bg-brand-soft text-brand'
                : 'bg-surface-2 text-ink-2',
          )}
        >
          {member.role === 'church_admin'
            ? 'Church Admin'
            : (member.role_name ?? 'No role')}
        </span>
      </td>
      <td className="px-s4 py-s3 text-right">
        {/* A church admin's own standing is not editable from here — demoting
            the person running the church would lock them out of this screen. */}
        {!isAdmin || member.role === 'church_admin' ? null : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="grid h-8 w-8 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2"
                aria-label={`Manage ${member.name}`}
              >
                <MoreHorizontal className="h-5 w-5" aria-hidden />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className="z-50 min-w-[240px] rounded-md border border-line-2 bg-surface p-s1 shadow-3"
              >
                {/* EVERY ROLE THE CHURCH HAS DEFINED, not a moderator
                    toggle. Roles are defined once on the Permissions screen
                    and held per group, so this is where an admin hands out
                    "Youth Lead" — the toggle could only ever express the two
                    seeded ones. */}
                <DropdownMenu.Label className="px-s3 pb-s1 pt-s2 text-sm font-semibold text-ink-2">
                  Role in this group only
                </DropdownMenu.Label>
                {roles.data?.map((role) => (
                  <DropdownMenu.Item
                    key={role.id}
                    onSelect={() =>
                      setRole.mutate({ userId: member.id, roleId: role.id })
                    }
                    className="flex cursor-pointer items-center gap-s2 rounded px-s3 py-s2 text-base outline-none data-[highlighted]:bg-hover-2"
                  >
                    {member.role_name === role.name ? (
                      <Check className="h-4 w-4 text-brand" aria-hidden />
                    ) : (
                      <span className="h-4 w-4" aria-hidden />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate">{role.name}</span>
                      {role.description ? (
                        <span className="block truncate text-sm text-ink-2">
                          {role.description}
                        </span>
                      ) : null}
                    </span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </td>
    </tr>
  )
}

/** The pending join requests for this group.
 *
 * Only rendered for somebody who can answer them — a church admin or this
 * group's moderator — because the endpoint behind it refuses anyone else.
 *
 * Approving is the mirror of accepting an invitation: it creates the
 * membership. Declining closes the request without one, and either way the
 * person is told, so a request never just goes quiet.
 */
function JoinRequestsCard({
  churchId,
  groupId,
}: {
  churchId: string
  groupId: string
}) {
  const requests = useJoinRequests(churchId, groupId)
  const answer = useAnswerJoinRequest(churchId, groupId)
  const [acting, setActing] = useState<string | null>(null)

  if (requests.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }

  if (!requests.data?.length) {
    return (
      <Card className="px-s4 py-s6 text-center text-base text-ink-2">
        No one is waiting to join. When somebody asks, they appear here for you
        to approve or decline.
      </Card>
    )
  }

  function respond(requestId: string, approve: boolean) {
    setActing(requestId)
    answer.mutate({ requestId, approve }, { onSettled: () => setActing(null) })
  }

  return (
    <ul className="flex flex-col gap-s2">
      {requests.data.map((r) => (
        <li key={r.id} className="cn-card flex items-center gap-s3 px-s4 py-s3">
          <Avatar name={r.name} src={r.avatar_url} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-md font-semibold">{r.name}</span>
            <span className="block truncate text-sm text-ink-2">
              {r.mobile ?? ''} · asked {timeAgo(r.created_at)}
            </span>
          </span>
          <div className="flex shrink-0 gap-s2">
            <Button
              variant="secondary"
              disabled={acting === r.id}
              onClick={() => respond(r.id, false)}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              loading={acting === r.id}
              onClick={() => respond(r.id, true)}
            >
              Approve
            </Button>
          </div>
        </li>
      ))}
    </ul>
  )
}
